// Daten-Tab — Crypto/HL Tickers + Makro-Grid + FNG-Gauge + Heute-Macro.
//
// Hybrid-Datenquellen:
//   - Crypto (BTC/ETH/SOL): direkt aus useHyperliquidStore (WS-Live mit
//     ~1s-Latenz). Funding/Change/OI aus assetCtx wenn vorhanden.
//   - Makro + FNG: aus useMarketStore (Worker-Cron 60s).

import { useMemo } from 'react';
import { deriveMarketStats } from '@/lib/market-cache';
import { TodayMacroBanner } from '@/components/markets/today-macro-banner';
import {
  selectIsConnected as selectHlConnected,
  selectLiveMetrics,
  selectLivePrices,
  useHyperliquidStore,
} from '@/stores/hyperliquid-store';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import type { HLAssetCtx, HLLiveMetrics, HLLivePrices } from '@/types/hyperliquid';
import type { FullData, MarketStats } from '@/types/market';

export function TabDaten() {
  const fullData = useMarketStore(selectFullData);
  const stats = useMemo<MarketStats | null>(() => deriveMarketStats(fullData), [fullData]);
  const livePrices = useHyperliquidStore(selectLivePrices);
  const liveMetrics = useHyperliquidStore(selectLiveMetrics);
  const isHlConnected = useHyperliquidStore(selectHlConnected);

  return (
    <div className="space-y-3">
      <CryptoCard prices={livePrices} metrics={liveMetrics} connected={isHlConnected} />
      <MacroCard fullData={fullData} />
      <FngGauge stats={stats} />
      <TodayMacroBanner variant="rail" highOnly={false} limit={5} />
    </div>
  );
}

interface CryptoCardProps {
  prices: HLLivePrices;
  metrics: HLLiveMetrics;
  connected: boolean;
}

const CRYPTO_SYMBOLS: ReadonlyArray<'BTC' | 'ETH' | 'SOL'> = ['BTC', 'ETH', 'SOL'];

function CryptoCard({ prices, metrics, connected }: CryptoCardProps) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">
          Crypto · Hyperliquid {connected ? '' : '· REST'}
        </span>
        <span
          className={`size-1.5 rounded-full ${
            connected ? 'animate-pulse bg-emerald-400' : 'bg-neutral-600'
          }`}
        />
      </div>
      <div className="space-y-1 text-xs">
        {CRYPTO_SYMBOLS.map((sym) => (
          <CryptoRow key={sym} sym={sym} priceStr={prices[sym]} ctx={metrics[sym] ?? null} />
        ))}
      </div>
    </div>
  );
}

function CryptoRow({
  sym,
  priceStr,
  ctx,
}: {
  sym: string;
  priceStr: string | undefined;
  ctx: HLAssetCtx | null;
}) {
  // WS-Mark bevorzugen (latency 1s), Fallback auf mid-price-String.
  const markPx = ctx?.markPx ?? (priceStr ? parseFloat(priceStr) : null);
  const change = ctx?.change ?? null;
  const funding = ctx?.funding ?? null;
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono">{sym}</span>
      <div className="text-right">
        <div className="tabular text-neutral-200">{fmtPrice(markPx, true)}</div>
        <div className={`text-[11px] tabular ${changeColor(change)}`}>
          {fmtChange(change)}
          {funding !== null ? ` · Funding ${fmtFundingPct(funding)}` : ''}
        </div>
      </div>
    </div>
  );
}

function fmtFundingPct(funding: number): string {
  if (!Number.isFinite(funding)) return '—';
  // funding ist Decimal (z.B. 0.0000089 = 0.0009%). ×100 für %.
  const pct = funding * 100;
  return `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(4)}%`;
}

interface MacroCardProps {
  fullData: FullData | null;
}

/** Erweitert von 4 auf 11 Symbole, gruppiert: Equity·Macro·Rates. */
function MacroCard({ fullData }: MacroCardProps) {
  const m = fullData?.macro ?? null;
  const groups: ReadonlyArray<{
    title: string;
    rows: ReadonlyArray<{ label: string; price: number | null | undefined; change: number | string | null | undefined; currency?: boolean; emphasis?: boolean }>;
  }> = [
    {
      title: 'Equity / Vola',
      rows: [
        { label: 'VIX', price: m?.vix?.price, change: m?.vix?.change, emphasis: true },
        { label: 'SPX', price: m?.spx?.price, change: m?.spx?.change },
      ],
    },
    {
      title: 'Macro / Commodities',
      rows: [
        { label: 'DXY', price: m?.dxy?.price, change: m?.dxy?.change },
        { label: 'GOLD', price: m?.gold?.price, change: m?.gold?.change, currency: true },
        { label: 'SILVER', price: m?.silver?.price, change: m?.silver?.change, currency: true },
        { label: 'OIL', price: m?.oil?.price, change: m?.oil?.change, currency: true },
        { label: 'OVX', price: m?.ovx?.price, change: m?.ovx?.change },
        { label: 'TLT', price: m?.tlt?.price, change: m?.tlt?.change, currency: true },
      ],
    },
    {
      title: 'Rates / FX',
      rows: [
        { label: 'US10Y', price: m?.us10y?.price, change: m?.us10y?.change },
        { label: 'US3M', price: m?.us3m?.price, change: m?.us3m?.change },
        { label: 'USDJPY', price: m?.jpy?.price, change: m?.jpy?.change },
      ],
    },
  ];

  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">Makro</span>
        <span className="text-[10px] text-neutral-600">11 Symbole</span>
      </div>
      <div className="space-y-3 text-[13px]">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-600">
              {g.title}
            </div>
            <div className="space-y-1">
              {g.rows.map((row) => (
                <div key={row.label} className="flex items-center justify-between">
                  <span className="text-neutral-500">{row.label}</span>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`tabular ${
                        row.emphasis ? 'text-amber-400' : 'text-neutral-200'
                      }`}
                    >
                      {fmtPrice(row.price, row.currency)}
                    </span>
                    <span className={`min-w-[3rem] text-right text-[11px] tabular ${changeColor(row.change)}`}>
                      {fmtChange(row.change)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FngGauge({ stats }: { stats: MarketStats | null }) {
  const value = stats?.fng?.value;
  const label = stats?.fng?.label;
  const pctVal = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : null;
  const dash = pctVal != null ? `${pctVal} 100` : '0 100';
  return (
    <div className="glass flex flex-col items-center rounded-xl p-3">
      <div className="mb-1 self-start text-[12px] uppercase tracking-wider text-neutral-500">F&G</div>
      <div className="relative my-1 size-16">
        <svg className="size-16 -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="3"
          />
          <path
            d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32"
            fill="none"
            stroke="#fbbf24"
            strokeWidth="3"
            strokeDasharray={dash}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center text-xl font-bold tabular">
          {value ?? '—'}
        </div>
      </div>
      <div className="text-[11px] uppercase text-amber-300">{label ?? '—'}</div>
    </div>
  );
}

// ── helpers ──
function fmtPrice(v: number | null | undefined, currency = false): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const formatted = v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return currency ? `$${formatted}` : formatted;
}

function fmtChange(v: number | string | null | undefined): string {
  const n = typeof v === 'string' ? Number.parseFloat(v) : v;
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function changeColor(v: number | string | null | undefined): string {
  const n = typeof v === 'string' ? Number.parseFloat(v) : v;
  if (typeof n !== 'number' || !Number.isFinite(n)) return 'text-neutral-500';
  return n >= 0 ? 'text-emerald-400' : 'text-rose-400';
}

